import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Button, CalendarPicker, CheckList, Dropdown, SearchBar, Toast} from 'antd-mobile';
import {CloseCircleFill, DownOutline, ScanningOutline} from 'antd-mobile-icons';
import type {DropdownRef} from 'antd-mobile';
import {useTranslation} from 'react-i18next';
import {useBarCodeScanner} from '../../../../../components/BarCodeScanner';
import {useNetwork} from '../../../../../hooks/useNetwork';
import {showNetworkErrorModal} from '../../../../../components/NetworkErrorModal';
import {isTrackingNumber} from '../../../../../utils/BarCodeUtil';
import {playScanSound} from '../../../../../utils/scanUtils';

const MAX_RANGE_DAYS = 14;
const MAX_LOOKBACK_DAYS = 90;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

interface Props {
    keyword: string;
    onKeywordChange: (v: string) => void;
    dateRange: [Date, Date] | null;
    onDateRangeChange: (v: [Date, Date] | null) => void;
    selectedType: string | null;
    onSelectedTypeChange: (v: string | null) => void;
    complaintTypeOptions: string[];
    searchPlaceholder?: string;
    /** 日期过滤维度文案：列表页"下发时间"、历史页"完结时间" */
    dateRangeLabel?: string;
}

const ComplaintFilterBar: React.FC<Props> = ({
    keyword,
    onKeywordChange,
    dateRange,
    onDateRangeChange,
    selectedType,
    onSelectedTypeChange,
    complaintTypeOptions,
    searchPlaceholder,
    dateRangeLabel,
}) => {
    const {t} = useTranslation();

    const typeDropdownRef = useRef<DropdownRef>(null);
    const [datePickerVisible, setDatePickerVisible] = useState(false);

    // 扫码（参考 DspSearchBar）：点扫码图标拉起全局扫码层，扫到运单号回填关键词并查询；离线拦截
    const {isVisible, setIsVisible, scanResult, setScanResult} = useBarCodeScanner();
    const {isOnline} = useNetwork();

    // 进入本页隐藏全局扫码层（仅点图标时主动拉起），离开恢复
    useEffect(() => {
        setIsVisible(false);
        return () => {
            setIsVisible(true);
        };
    }, [setIsVisible]);

    const handleScanClick = () => {
        if (!isOnline) {
            showNetworkErrorModal();
            return;
        }
        setIsVisible(!isVisible);
    };

    useEffect(() => {
        if (scanResult && isTrackingNumber(scanResult)) {
            playScanSound();
            onKeywordChange(scanResult);
            setIsVisible(false);
            // 重置 store 的 scanResult/lastScanResult，避免再次扫同一运单号时被去重拦截、无法重新触发回填与关闭
            setScanResult('');
        }
    }, [scanResult, onKeywordChange, setIsVisible, setScanResult]);

    const {minDate, maxDate} = useMemo(() => {
        const max = new Date();
        max.setHours(23, 59, 59, 999);
        const min = new Date();
        min.setDate(min.getDate() - (MAX_LOOKBACK_DAYS - 1));
        min.setHours(0, 0, 0, 0);
        return {minDate: min, maxDate: max};
    }, []);

    const handleDateConfirm = (val: [Date, Date] | null) => {
        if (val) {
            const [start, end] = val;
            const diffDays = Math.floor((end.getTime() - start.getTime()) / MS_PER_DAY) + 1;
            if (diffDays > MAX_RANGE_DAYS) {
                Toast.show(
                    t('pages.dsp.home.customerComplaint.list.dateRangeLimit', {
                        days: MAX_RANGE_DAYS,
                        defaultValue: '最多可选 {{days}} 天',
                    }) as string,
                );
                return;
            }
        }
        onDateRangeChange(val);
        setDatePickerVisible(false);
    };

    const renderDateRangeLabel = () => {
        if (!dateRange) {
            return (
                dateRangeLabel ?? t('pages.dsp.home.customerComplaint.list.dropdownDate', '下发时间')
            );
        }
        const pad = (n: number) => String(n).padStart(2, '0');
        const fmt = (d: Date) => `${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;
        return `${fmt(dateRange[0])}-${fmt(dateRange[1])}`;
    };

    const translateType = (code: string) =>
        t(`pages.dsp.home.customerComplaint.complaintType.${code}`, code) as string;

    const renderTypeLabel = () =>
        selectedType
            ? translateType(selectedType)
            : t('pages.dsp.home.customerComplaint.list.dropdownType', '问题件类型');

    const dateActive = !!dateRange || datePickerVisible;

    return (
        <>
            <div className="px-3 pt-2 pb-1 flex items-center">
                <div className="flex-1">
                    <SearchBar
                        placeholder={
                            searchPlaceholder ??
                            (t(
                                'pages.dsp.home.customerComplaint.list.searchPlaceholder',
                                '请输入包裹号',
                            ) as string)
                        }
                        value={keyword}
                        onChange={onKeywordChange}
                    />
                </div>
                <Button onClick={handleScanClick} fill="none" size="large">
                    <ScanningOutline />
                </Button>
            </div>

            <div className="flex border-b border-gray-200 bg-white">
                <div
                    className="flex-1 flex items-center justify-center py-3 text-sm cursor-pointer"
                    style={{color: dateActive ? 'var(--adm-color-primary)' : undefined}}
                    onClick={() => setDatePickerVisible(true)}
                >
                    <span>{renderDateRangeLabel()}</span>
                    {dateRange ? (
                        <CloseCircleFill
                            className="ml-1 text-gray-400"
                            fontSize={14}
                            onClick={(e) => {
                                e.stopPropagation();
                                onDateRangeChange(null);
                            }}
                        />
                    ) : (
                        <DownOutline className="ml-1" fontSize={12} />
                    )}
                </div>
                <Dropdown ref={typeDropdownRef} className="flex-1">
                    <Dropdown.Item key="type" title={renderTypeLabel()}>
                        <CheckList
                            defaultValue={selectedType ? [selectedType] : []}
                            onChange={(v) => onSelectedTypeChange(v[0] ? String(v[0]) : null)}
                        >
                            <CheckList.Item value="">{t('pages.dsp.home.customerComplaint.list.filterTypeAll')}</CheckList.Item>
                            {complaintTypeOptions.map((typeCode) => (
                                <CheckList.Item key={typeCode} value={typeCode}>
                                    {translateType(typeCode)}
                                </CheckList.Item>
                            ))}
                        </CheckList>
                    </Dropdown.Item>
                </Dropdown>
            </div>

            <CalendarPicker
                key={datePickerVisible ? 'open' : 'closed'}
                visible={datePickerVisible}
                selectionMode="range"
                defaultValue={dateRange ?? undefined}
                min={minDate}
                max={maxDate}
                allowClear
                onClose={() => setDatePickerVisible(false)}
                onConfirm={(val) => handleDateConfirm(val as [Date, Date] | null)}
            />
        </>
    );
};

export default ComplaintFilterBar;
